import React, { useState, useRef } from 'react';
// import ReactDOM from 'react-dom';
import { toRomaji, isJapanese, isKana } from 'wanakana';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// ICONS
/*
import AirIcon from '@mui/icons-material/Air';
import SevereColdIcon from '@mui/icons-material/SevereCold';
import StormIcon from '@mui/icons-material/Storm';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import WbSunny from '@mui/icons-material/WbSunny';
import AcUnit from '@mui/icons-material/AcUnit';
import Cloud from '@mui/icons-material/Cloud';
import Cyclone from '@mui/icons-material/Cyclone';
import Flood from '@mui/icons-material/Flood';
import Landslide from '@mui/icons-material/Landslide';
import ModeNight from '@mui/icons-material/ModeNight';
import Nightlight from '@mui/icons-material/Nightlight';
import Thunderstorm from '@mui/icons-material/Thunderstorm';
import Tornado from '@mui/icons-material/Tornado';
import Tsunami from '@mui/icons-material/Tsunami';
import Water from '@mui/icons-material/Water';
import NightsStay from '@mui/icons-material/NightsStay';
*/

const API_KEY = process.env.REACT_APP_WEATHER_API_KEY

/*
const MOCK_ITEM = {
    "coord": {
        "lon": 139.6917,
        "lat": 35.6895
    },
    "weather": [
        {
            "id": 803,
            "main": "Clouds",
            "description": "broken clouds",
            "icon": "04d"
        }
    ],
    "base": "stations",
    "main": {
        "temp": 28.09,
        "feels_like": 31.56,
        "temp_min": 25.42,
        "temp_max": 30.42,
        "pressure": 1006,
        "humidity": 75
    },
    "visibility": 10000,
    "wind": {
        "speed": 2.57,
        "deg": 60
    },
    "clouds": {
        "all": 75
    },
    "dt": 1658616374,
    "sys": {
        "type": 2,
        "id": 2038398,
        "country": "JP",
        "sunrise": 1658605354,
        "sunset": 1658656354
    },
    "timezone": 32400,
    "id": 1850144,
    "name": "Tokyo",
    "cod": 200
}
*/
// https://openweathermap.org/current#name

const getWeatherAPIURL = (cityName: string, API_KEY: string) => `https://api.openweathermap.org/data/2.5/weather?q=${cityName}&units=metric&appid=${API_KEY}`

interface WeatherDataProperties {
    cityName: string,
    description: string,
    temp: number,
    feels_like: number,
    temp_min: number,
    temp_max: number,
    pressure: number,
    humidity: number,
    wind_speed: number,
    wind_deg: number,
    cloud_coverage: number
}
interface WeatherData extends WeatherDataProperties {
    // Indexing
    key: number
}
type WeatherDataKey = keyof WeatherData;
type WeatherDataPropertiesKey = keyof WeatherDataProperties;
type WeatherDataMessages = Record<WeatherDataPropertiesKey, string>;

const WEATHER_DATA_EN: WeatherDataMessages = {
    cityName: 'City Name',
    description: 'Description',
    temp: 'Temperature',
    feels_like: 'Feels Like',
    temp_min: 'Min Temperature',
    temp_max: 'Maximum Temperature',
    pressure: 'Air Pressure',
    humidity: 'Humidity',
    wind_speed: 'Wind Speed',
    wind_deg: 'Wind Degree',
    cloud_coverage: 'Cloud Coverage',
}
const WEATHER_DATA_JA: WeatherDataMessages = {
    cityName: '市町村',
    description: '説明',
    temp: '温度',
    feels_like: '実感温度',
    temp_min: '最低温度',
    temp_max: '最高温度',
    pressure: '気圧',
    humidity: '湿度',
    wind_speed: '風速',
    wind_deg: '風方向',
    cloud_coverage: '雲量',
}
const WEATHER_DATA = {
    en: WEATHER_DATA_EN,
    ja: WEATHER_DATA_JA,
} as const;
const DISPLAYED_KEYS: WeatherDataPropertiesKey[] = [
    'cityName',
    'description',
    'temp',
    'feels_like',
    'temp_min',
    'temp_max',
    'pressure',
    'humidity',
    'wind_speed',
    'wind_deg',
    'cloud_coverage',
];
type Language = keyof typeof WEATHER_DATA;
const LANGUAGES = Object.keys(WEATHER_DATA) as Language[];
const language: Language = getRestrictedGETProperty('language', LANGUAGES, 'en') as Language;

const CURRENT_WEATHER_DATA = WEATHER_DATA[language];

const GENERAL_MESSAGES = {
    en: {
        title: 'City Weather Search',
        city: 'City',
        search: 'Search',
    },
    ja: {
        title: '市町村の天気予報検索',
        city: '市町村',
        search: '検索',
    }
} as const;
const M = GENERAL_MESSAGES[language];

function getRestrictedGETProperty(key: string, allowedValues: string[], defaultValue?: string) {
    let value = defaultValue;
    const getValue = (new URL(window.location.href)).searchParams.get(key);
    if (!([null, undefined] as any[]).includes(getValue) && allowedValues.includes(getValue as any)) {
        value = getValue as string;
    }
    console.debug('getRestrictedGETProperty', { getValue, value }) // DEBUG
    return value;
}

let keyIndex = 0;
const newKey = () => {
    const key = keyIndex
    keyIndex++
    return key
}

function parseAPIResponse(response: any) {
    const weatherData: WeatherData = {
        // Indexing
        key: newKey(),
        // Data
        cityName: response.name,
        description: response.weather[0].description,
        temp: response.main.temp,
        feels_like: response.main.feels_like,
        temp_min: response.main.temp_min,
        temp_max: response.main.temp_max,
        pressure: response.main.pressure,
        humidity: response.main.humidity,
        wind_speed: response.wind.speed,
        wind_deg: response.wind.deg,
        cloud_coverage: response.clouds.all
    }
    return weatherData
}

async function getWeatherData(cityName: string) {
    if (!API_KEY) {
        throw new Error('API_KEY required')
    }
    if (isJapanese(cityName)) {
        console.debug(`is japanese: ${cityName}`)
        if (isKana(cityName)) {
            cityName = toRomaji(cityName)
            console.warn('Currently, kana support is limited to easily auto-converted words.')
        } else {
            throw new Error('Currently Kanji not supported')
        }
    }
    console.debug('city name:', cityName)
    const url = getWeatherAPIURL(cityName, API_KEY)
    const response = await fetch(url)
    if (!response.ok) {
        throw new Error(JSON.stringify(await response.json()))
    }
    const obj = await(response).json()
    return parseAPIResponse(obj)
}

export default function WeatherApp() {
    const [items, setItems] = useState<WeatherData[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const ref = useRef<HTMLInputElement>(document.createElement('input'))

    // const errorMessageNotify = () => toast(errorMessage);
    const errorMessageNotify = (m: string) => toast(m);

    const updateWeatherData = async() => {
        const cityName = ref.current.value
        try {
            const item = await getWeatherData(cityName)
            // const item: WeatherData = parseAPIResponse(MOCK_ITEM) // DEBUG
            setItems([item, ...items])
            setErrorMessage('');
        } catch (err) {
            const m = (err as Error).message;
            console.debug('error', { err, m })
            setErrorMessage(m);
            errorMessageNotify(m);
        }
        // console.debug({ cityName }) // DEBUG
        
    }
    const onClick = () => {
        updateWeatherData()
    }
    const onKeyDown = (ev: any) => {
        const key = ev.key
        if (key === 'Enter') {
            updateWeatherData()
        }
    }
    return (
        <div>
            <header>
                <h1 style={{ textAlign: 'center', margin: '10px' }}>{M.title}</h1>
            </header>
            <main>
                <form className='weather-form' onSubmit={(ev) => ev.preventDefault()}>
                    <label htmlFor="input">{M.city}: </label>
                    <input type="search" name="input" id="input" ref={ref} onKeyDown={onKeyDown} />
                    <input type="button" value={M.search} onClick={onClick} />
                </form>
                <WeatherComponentTable items={items} />
                <ToastContainer />
            </main>
            <footer>
                <ul>
                    {LANGUAGES.map((l) => <li><a href={`?language=${l}`}>{l}</a></li>)}
                </ul>
            </footer>
        </div>
    )
}

/**
 * @see https://openweathermap.org/weather-data
 * metric
 */
function WeatherComponentTable({ items }: { items: WeatherData[] }) {
    return (
        <table className='weather-table'>
            <thead>
                <tr>
                    {
                        DISPLAYED_KEYS.map((key) => <th key={key}>{CURRENT_WEATHER_DATA[key]}</th>)
                    }
                </tr>
            </thead>
            <tbody>
            {
                items.map(item => (
                    <tr key={item.key}>
                    {
                        DISPLAYED_KEYS.map((key) => <td key={key}>{item[key]}</td>)
                    }
                    </tr>
                ))
            }
            </tbody>
        </table>
    )
}
